//@flow
import * as Immutable from 'immutable';
import {
    ChecklistItem,
    SelectedChecklistItem,
    AdditionalOptions
} from 'common/models/flow/ChecklistItem';
import type {
    InputType,
    ChecklistItemModelType,
    SelectedChecklistItemModelType,
    AdditionalOptionsModelType
} from 'common/models/flow/ChecklistItem';
import { PodTypeEnums } from 'common/enums/VisitEnums';
import I18nHelper from 'common/helpers/I18nHelper';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';
import {
    validate,
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';

const nsTranslate = I18nHelper.getTranslate();

@validatable
class InternalChecklistItem extends ChecklistItem {
    static fromJS(json: ChecklistItemModelType): InternalChecklistItem {
        const state: Object = Object.assign({}, json);
        state.possibleValues = Immutable.List(state.possibleValues);
        state.allowedValues = Immutable.List(state.allowedValues);
        state.additionalOptions = state.additionalOptions
            ? InternalAdditionalOptions.fromJS(state.additionalOptions)
            : state.additionalOptions;
        state.valueMappings = Immutable.Map(state.valueMappings);
        return new this(Immutable.Map(state));
    }

    @validate
    setKey(key: string): this {
        return super.setKey(key);
    }

    validateKey(): ?string {
        if (!this.key) {
            return nsTranslate('validations:posChecklist.requiredFieldEmpty');
        }
    }

    @validate
    setFormat(format: string): this {
        return super.setFormat(format);
    }

    validateFormat(): ?string {
        if (!this.format) {
            return nsTranslate('validations:posChecklist.requiredFieldEmpty');
        }
    }

    @validate
    setItem(item: string): this {
        return super.setItem(item);
    }

    validateItem(): ?string {
        if (!this.item) {
            return this.validateChecklistItem(this.item);
        }
    }

    @validate
    setPossibleValues(possibleValues: Immutable.List<string>): this {
        return super.setPossibleValues(possibleValues);
    }

    validatePossibleValues(): ?string {
        if (
            [PodTypeEnums.SingleChoice, PodTypeEnums.MultiChoice].includes(
                this.format
            )
        ) {
            if (!this.possibleValues || !this.possibleValues.size) {
                return nsTranslate(
                    'selfServe:trackIQ.checklistSettings.validations.possibleValues'
                );
            }

            const hasDuplicate = this.possibleValues.some(
                (val, idx, arr) => arr.indexOf(val) !== idx
            );
            if (hasDuplicate) {
                return nsTranslate(
                    'selfServe:trackIQ.checklistSettings.validations.duplicateValue'
                );
            }
        }
        if (
            (this.format === PodTypeEnums.Rating ||
                this.format === PodTypeEnums.Url) &&
            (!this.possibleValues || !this.possibleValues.size)
        ) {
            return nsTranslate('validations:posChecklist.requiredFieldEmpty');
        }
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const hasError = this.additionalOptions?.hasValidationErrors();
        return !!hasError;
    }

    @validateAll
    setAllValidationErrors(): this {
        if (this.additionalOptions) {
            const updatedAdditionalOptions = this.additionalOptions.setAllValidationErrors();
            return this.setAdditionalOptions(updatedAdditionalOptions);
        }
        return this;
    }

    validateChecklistItem(value): ?string {
        if (!DataUtils.isBoolean(value) && DataUtils.isEmpty(value)) {
            if (!this.optional) {
                return nsTranslate(
                    'validations:posChecklist.requiredFieldEmpty'
                );
            }
            return;
        }
        switch (this.format) {
            case PodTypeEnums.Rating:
                return this.validateRating(value);
            default:
                return this.validateValue(value);
        }
    }
    getAbsoluteKey(visitId?: string) {
        if (!visitId) {
            return this.key;
        }
        return `${visitId}/${this.key}`;
    }
    validateValue(value) {
        const regexPattern = this.additionalOptions.get('regex');
        const inputType = this.additionalOptions.get('inputType');
        const errorMessage = this.additionalOptions.get('errorMessage');
        const regex = new RegExp(regexPattern);
        if (!regex.test(value)) {
            if (errorMessage) {
                return errorMessage;
            }
            if (inputType === 'NUMBER') {
                return nsTranslate('validations:posChecklist.notANumber');
            }
            return nsTranslate('validations:posChecklist.valueRegexFail', {
                regex: regexPattern
            });
        }
    }

    validateRating(value) {
        const [maxRating] = this.possibleValues;
        if (DataUtils.isEmpty(value) && !this.optional) {
            return;
        }
        if (!NumberUtils.isInteger(value)) {
            return nsTranslate('validations:posChecklist.ratingNotANumber');
        }
        const rating = parseInt(value);
        if (rating > maxRating || rating < 0) {
            return nsTranslate('validations:posChecklist.ratingOutOfRange', {
                maxRating
            });
        }
    }

    get additionalOptions(): ?InternalAdditionalOptions {
        return this._state.get('additionalOptions');
    }
}

class InternalSelectedChecklistItem extends SelectedChecklistItem {
    static fromJS(
        json: SelectedChecklistItemModelType
    ): InternalSelectedChecklistItem {
        const state: Object = Object.assign({}, json);
        state.selectedValues = state.selectedValues
            ? Immutable.List(state.selectedValues)
            : state.selectedValues;
        return new this(Immutable.Map(state));
    }
}

@validatable
class InternalAdditionalOptions extends AdditionalOptions {
    static fromJS(json: AdditionalOptionsModelType): InternalAdditionalOptions {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @validate
    setRegex(regex: string): this {
        return super.setRegex(regex);
    }

    validateRegex(): ?string {
        if (!this.regex) {
            return nsTranslate(
                'selfServe:trackIQ.checklistSettings.validations.regex'
            );
        }
    }

    @validate
    setInputType(inputType: InputType): this {
        return super.setInputType(inputType);
    }

    validateInputType(): ?string {
        if (!this.inputType) {
            return nsTranslate(
                'selfServe:trackIQ.checklistSettings.validations.inputType'
            );
        }
    }

    @validate
    setErrorMessage(errorMessage: string): this {
        return super.setErrorMessage(errorMessage);
    }

    validateErrorMessage(): ?string {
        if (!this.errorMessage) {
            return nsTranslate(
                'selfServe:trackIQ.checklistSettings.validations.errorMessage'
            );
        }
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }
}

export {
    InternalChecklistItem as ChecklistItem,
    InternalSelectedChecklistItem as SelectedChecklistItem,
    InternalAdditionalOptions as AdditionalOptions
};
